import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Trash2, Monitor, Mail, CheckCircle, Eye, Download,
         Briefcase, Building2, Hash, Phone, Calendar, Package, Wifi, PhoneCall, FileText, AlertTriangle, Wrench, ClipboardList, History } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import { isEmployeePaid, damagePayerLabel, damagePayerColor } from '../utils/damage';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import WarrantyBadge from '../components/WarrantyBadge';
import ItemHistoryList, { itemHistoryCount } from '../components/ItemHistoryList';
import SearchableSelect from '../components/SearchableSelect';

const STATUSES = ['active','resigned','terminated','transferred'];

// Inline elapsed-time badge for ticket history
function TicketElapsedBadge({ ticket }) {
  const start = ticket.reported_date || ticket.created_at;
  if (!start) return null;
  const active = ['open','in_progress','on_hold'].includes(ticket.status);
  const endMs  = active ? Date.now() : new Date(ticket.resolved_date || ticket.closed_date || ticket.updated_at).getTime();
  const hours  = Math.max(0, (endMs - new Date(start).getTime()) / 3600000);
  const days   = Math.floor(hours / 24);
  const remH   = Math.floor(hours % 24);
  const mins   = Math.floor((hours * 60) % 60);
  const label  = days > 0 ? `${days}d ${remH}h` : hours >= 1 ? `${Math.floor(hours)}h ${mins}m` : `${mins}m`;
  const color  = !active ? '#475569' : hours > 48 ? '#ef4444' : hours >= 8 ? '#f59e0b' : '#10b981';
  return (
    <span style={{ fontSize:10, fontWeight:700, padding:'2px 6px', borderRadius:6,
      background:`${color}18`, color, border:`1px solid ${color}35`, whiteSpace:'nowrap' }}>
      {active ? '⏱ ' : '✓ '}{label}
    </span>
  );
}
const SYS_TYPES = ['ERP System','CRM','HR System','Accounting','Email Server','VPN','Remote Desktop','POS System','Payroll','Document Management','Other'];

// ── IT Profile section (add/view system logins + emails) ──────────────────────
const ITProfileSection = ({ empId, isIT }) => {
  const [items, setItems] = useState({ systems: [], emails: [] });
  const [loading, setLoading] = useState(false);
  const [itTab, setItTab] = useState('systems');
  const [sysForm, setSysForm] = useState({ status:'active' });
  const [emailForm, setEmailForm] = useState({ status:'active', email_type:'corporate' });
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState({}); // id → password, auto-hidden after 30s
  const { user } = useAuth();
  const canReveal = ['admin', 'it_staff'].includes(user?.role);

  const hidePassword = (id) => setRevealed(prev => { const next = { ...prev }; delete next[id]; return next; });
  const revealPassword = async (type, id) => {
    if (revealed[id]) { hidePassword(id); return; }
    try {
      const r = await api.post(`/master/employee-items/${empId}/${type}/${id}/reveal-password`);
      setRevealed(prev => ({ ...prev, [id]: r.data.password }));
      setTimeout(() => hidePassword(id), 30000);
    } catch (err) { window.alert(err.response?.data?.error || 'Could not reveal the password'); }
  };

  const loadItems = async () => {
    setLoading(true);
    try { const r = await api.get(`/master/employee-items/${empId}`); setItems(r.data); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { if (empId) loadItems(); }, [empId]);

  const addSystem = async e => {
    e.preventDefault(); setSaving(true);
    try { await api.post(`/master/employee-items/${empId}/system`, sysForm); setSysForm({ status:'active' }); loadItems(); }
    catch {} finally { setSaving(false); }
  };

  const addEmail = async e => {
    e.preventDefault(); setSaving(true);
    try { await api.post(`/master/employee-items/${empId}/email`, emailForm); setEmailForm({ status:'active', email_type:'corporate' }); loadItems(); }
    catch {} finally { setSaving(false); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm('Remove?')) return;
    await api.delete(`/master/employee-items/${empId}/${type}/${id}`).catch(() => {});
    loadItems();
  };

  const ItemRow = ({ item, type }) => (
    <div className="flex items-center gap-3 px-3 py-2 rounded-lg group"
      style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.05)'}}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate" style={{color:'#e2e8f0'}}>{item.system_name||item.email_address}</div>
        <div className="text-xs truncate" style={{color:'#475569'}}>
          {item.username && `User: ${item.username} · `}
          {item.system_url && <a href={item.system_url} target="_blank" rel="noreferrer" style={{color:'#60a5fa',marginRight:4}}>{item.system_url.replace(/^https?:\/\//,'')}</a>}
          {item.access_level && `Access: ${item.access_level} · `}
          {item.password_set && (
            <span style={{color:'#f59e0b',marginRight:4}}>
              🔑 {revealed[item.id] ? <code style={{color:'#fbbf24',userSelect:'all'}}>{revealed[item.id]}</code> : 'Password set'}
              {canReveal && (
                <button type="button" onClick={() => revealPassword(type, item.id)} className="ml-1 underline" style={{color:'#60a5fa'}}
                  title="Reveal the stored initial password (recorded in the Audit Trail)">
                  {revealed[item.id] ? 'Hide' : 'Reveal'}
                </button>
              )}
              {' · '}
            </span>
          )}
          {item.email_type && `${item.email_type} · `}
          {item.approved_by && <span style={{color:'#60a5fa'}}>Approved by: {item.approved_by}</span>}
        </div>
      </div>
      <Badge value={item.status||'active'}/>
      {isIT && <button onClick={() => deleteItem(type, item.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-xs px-1.5 py-0.5 rounded" style={{color:'#ef4444',background:'rgba(239,68,68,0.1)'}}>×</button>}
    </div>
  );

  if (loading) return <div className="py-4 text-center text-xs" style={{color:'#334155'}}>Loading IT profile...</div>;

  return (
    <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(129,140,248,0.2)'}}>
      <div className="px-4 py-3 flex items-center gap-2" style={{background:'rgba(129,140,248,0.08)'}}>
        <Monitor size={13} style={{color:'#818cf8'}}/>
        <span className="text-sm font-semibold" style={{color:'#818cf8'}}>IT Profile</span>
        <span className="text-xs ml-2" style={{color:'#334155'}}>auto-syncs to Access Management</span>
      </div>
      <div className="flex" style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
        {[['systems','System Logins',items.systems?.length||0],['emails','Email IDs',items.emails?.length||0]].map(([key,label,count]) => (
          <button key={key} type="button" onClick={() => setItTab(key)}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium transition-all"
            style={{background:itTab===key?'rgba(59,130,246,0.12)':'transparent',color:itTab===key?'#60a5fa':'#475569',borderBottom:itTab===key?'2px solid #3b82f6':'2px solid transparent'}}>
            {key==='systems'?<Monitor size={11}/>:<Mail size={11}/>}
            {label} <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs" style={{background:'rgba(255,255,255,0.06)',color:'#475569'}}>{count}</span>
          </button>
        ))}
      </div>
      <div className="p-4 space-y-2">
        {itTab === 'systems' && (
          <>
            {items.systems?.map(s => <ItemRow key={s.id} item={s} type="system"/>)}
            {isIT && (
              <form onSubmit={addSystem} className="p-3 rounded-xl space-y-2 mt-2" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.15)'}}>
                <div className="text-xs font-semibold" style={{color:'#60a5fa'}}>Add System Login</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>System Type *</label>
                    <select required value={sysForm.system_name||''} onChange={e=>setSysForm({...sysForm,system_name:e.target.value})} className="input-dark" style={{fontSize:12}}>
                      <option value="">Select type</option>
                      {SYS_TYPES.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>System URL</label>
                    <input type="text" value={sysForm.system_url||''} onChange={e=>setSysForm({...sysForm,system_url:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="e.g. https://app.example.com"/></div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Username</label>
                    <input value={sysForm.username||''} onChange={e=>setSysForm({...sysForm,username:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="Login username"/></div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Initial Password</label>
                    <input type="text" value={sysForm.initial_password||''} onChange={e=>setSysForm({...sysForm,initial_password:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="Temp password (user must reset)"/></div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Access Level</label>
                    <input value={sysForm.access_level||''} onChange={e=>setSysForm({...sysForm,access_level:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="e.g. Admin, Read-only"/></div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Approved By *</label>
                    <input required value={sysForm.approved_by||''} onChange={e=>setSysForm({...sysForm,approved_by:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="Approver's name"/></div>
                </div>
                <button type="submit" disabled={saving} className="btn-primary text-xs w-full justify-center">{saving?'Adding...':'Add System Login'}</button>
              </form>
            )}
          </>
        )}
        {itTab === 'emails' && (
          <>
            {items.emails?.map(e => <ItemRow key={e.id} item={e} type="email"/>)}
            {isIT && (
              <form onSubmit={addEmail} className="p-3 rounded-xl space-y-2 mt-2" style={{background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)'}}>
                <div className="text-xs font-semibold" style={{color:'#34d399'}}>Add Email ID</div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Email Address *</label>
                    <input required type="email" value={emailForm.email_address||''} onChange={e=>setEmailForm({...emailForm,email_address:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="user@company.com"/></div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Type</label>
                    <select value={emailForm.email_type||'corporate'} onChange={e=>setEmailForm({...emailForm,email_type:e.target.value})} className="input-dark" style={{fontSize:12}}>
                      {['corporate','personal','shared','alias'].map(t=><option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Initial Password</label>
                    <input type="text" value={emailForm.initial_password||''} onChange={e=>setEmailForm({...emailForm,initial_password:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="Temp password (user must reset)"/></div>
                  <div><label className="block text-xs font-medium mb-0.5" style={{color:'#475569'}}>Approved By *</label>
                    <input required value={emailForm.approved_by||''} onChange={e=>setEmailForm({...emailForm,approved_by:e.target.value})} className="input-dark" style={{fontSize:12}} placeholder="Approver's name"/></div>
                </div>
                <button type="submit" disabled={saving} className="btn-primary text-xs w-full justify-center">{saving?'Adding...':'Add Email ID'}</button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── PDF generator — A4, SynergyNode branded ───────────────────────────────────
function downloadProfilePDF(emp, assets, accessories, itItems, voice, dongles) {
  const domain = window.location.origin;
  const logoUrl = `${domain}/api/logo`;
  const genDate = new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' });

  const tblRows = (arr, cols) => arr.length === 0
    ? `<tr><td colspan="${cols}" style="padding:10px;text-align:center;color:#94a3b8;font-style:italic;font-size:11px;">None on record</td></tr>`
    : arr.map(r => `<tr>${r}</tr>`).join('');


  const html = `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8">
<title>Employee IT Profile — ${emp.full_name}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  @page{size:A4;margin:15mm 14mm}
  body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#1e293b;background:#fff;padding:0}
  .page{width:182mm;min-height:267mm;padding:0;position:relative}

  /* Header */
  .hdr{display:flex;justify-content:space-between;align-items:center;padding:10px 0 10px;border-bottom:3px solid #0096c7;margin-bottom:14px}
  .hdr-logo img{height:44px;max-width:200px;display:block}
  .hdr-right{text-align:right}
  .doc-title{font-size:14px;font-weight:700;color:#1d4ed8;margin-bottom:2px}
  .doc-meta{font-size:9px;color:#64748b}

  /* Employee banner */
  .emp-banner{background:linear-gradient(135deg,#1d4ed8,#0096c7);color:#fff;padding:14px 16px;border-radius:6px;margin-bottom:14px;display:flex;justify-content:space-between;align-items:flex-start}
  .emp-name{font-size:18px;font-weight:700;letter-spacing:-0.3px}
  .emp-role{font-size:11px;opacity:0.85;margin-top:2px}
  .emp-badge{background:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.4);padding:3px 10px;border-radius:12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;white-space:nowrap}

  /* Info grid */
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:4px 24px;margin-bottom:14px;background:#f8fafc;padding:10px 12px;border-radius:4px;border:1px solid #e2e8f0}
  .info-item{display:flex;gap:6px;font-size:10.5px}
  .info-label{color:#64748b;min-width:90px;font-weight:600;flex-shrink:0}
  .info-val{color:#1e293b}

  /* Section tables */
  table{width:100%;border-collapse:collapse;margin-bottom:12px;font-size:10.5px}
  thead tr{background:#1d4ed8}
  thead th{color:#fff;padding:6px 8px;text-align:left;font-weight:600;font-size:10px;text-transform:uppercase;letter-spacing:0.4px}
  tbody tr:nth-child(even){background:#f8fafc}
  tbody tr:hover{background:#eff6ff}
  td{padding:5px 8px;border-bottom:1px solid #e2e8f0;vertical-align:top;line-height:1.4}
  .mono{font-family:'Courier New',monospace;font-size:10px}

  /* Badges */
  .badge{display:inline-block;padding:2px 7px;border-radius:10px;font-size:9.5px;font-weight:600;text-transform:capitalize}
  .b-active,.b-in_use{background:#dcfce7;color:#166534}
  .b-available{background:#dbeafe;color:#1e40af}
  .b-resolved,.b-completed{background:#f0fdf4;color:#166534}
  .b-expired,.b-broken,.b-lost{background:#fee2e2;color:#991b1b}
  .b-in_repair,.b-pending{background:#fef3c7;color:#92400e}
  .b-inactive,.b-resigned,.b-terminated{background:#f1f5f9;color:#475569}

  /* Section header */
  .sect-hdr{background:#1d4ed8;color:#fff;font-size:10px;font-weight:700;padding:5px 10px;letter-spacing:0.6px;text-transform:uppercase;margin-bottom:0;border-radius:4px 4px 0 0}

  /* Footer */
  .footer{margin-top:16px;padding-top:10px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:9px;color:#94a3b8}

  @media print{
    body{padding:0}
    .no-print{display:none!important}
    table{page-break-inside:auto}
    tr{page-break-inside:avoid}
  }
</style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="hdr">
    <div class="hdr-logo">
      <img src="${logoUrl}" alt="SynergyNode" onerror="this.outerHTML='<span style=&quot;font-family:Georgia,serif;font-size:20px;font-weight:bold;color:#1d4ed8;&quot;>Synergy<span style=&quot;color:#0096c7;&quot;>Node</span></span>'"/>
      <div style="font-size:9px;color:#94a3b8;margin-top:2px;">A CL Synergy Group Product</div>
    </div>
    <div class="hdr-right">
      <div class="doc-title">Employee IT Profile</div>
      <div class="doc-meta">Generated: ${genDate}</div>
      <div class="doc-meta">Ref: ${emp.employee_code||'—'} &nbsp;·&nbsp; ${emp.company?.name||'—'}</div>
    </div>
  </div>

  <!-- Employee banner -->
  <div class="emp-banner">
    <div>
      <div class="emp-name">${emp.full_name}</div>
      <div class="emp-role">${emp.designation||''}${emp.designation&&emp.department?.name?' · ':''}${emp.department?.name||''}</div>
    </div>
    <div class="emp-badge">${emp.status||'active'}</div>
  </div>

  <!-- Info grid -->
  <div class="info-grid">
    <div class="info-item"><span class="info-label">Employee Code</span><span class="info-val mono">${emp.employee_code||'—'}</span></div>
    <div class="info-item"><span class="info-label">Company</span><span class="info-val">${emp.company?.name||'—'}</span></div>
    <div class="info-item"><span class="info-label">Email</span><span class="info-val">${emp.email||'—'}</span></div>
    <div class="info-item"><span class="info-label">Department</span><span class="info-val">${emp.department?.name||'—'}</span></div>
    <div class="info-item"><span class="info-label">Phone</span><span class="info-val">${emp.phone||'—'}</span></div>
    <div class="info-item"><span class="info-label">Date Joined</span><span class="info-val">${emp.date_joined||'—'}</span></div>
    ${emp.hod ? `<div class="info-item"><span class="info-label">HOD</span><span class="info-val">${emp.hod.full_name}${emp.hod.designation?` (${emp.hod.designation})`:''}</span></div>` : ''}
    ${emp.hod_email ? `<div class="info-item"><span class="info-label">HOD Email</span><span class="info-val">${emp.hod_email}</span></div>` : ''}
    ${emp.remarks ? `<div class="info-item" style="grid-column:1/-1"><span class="info-label">Remarks</span><span class="info-val">${emp.remarks}</span></div>` : ''}
  </div>

  <!-- Assets -->
  <div class="sect-hdr">Assigned Assets &nbsp;<span style="font-weight:400;opacity:0.8">(${assets.length})</span></div>
  <table>
    <thead><tr><th>Asset No</th><th>Category</th><th>Brand / Model</th><th>Serial No</th><th>Purchased</th><th>Allocated</th><th>Status</th></tr></thead>
    <tbody>
    ${tblRows(assets.map(a=>`
      <td class="mono">${a.asset_number||'—'}</td>
      <td>${a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())||'—'}</td>
      <td>${a.brand||''} ${a.model||''}</td>
      <td class="mono">${a.serial_number||a.imei_1||'—'}</td>
      <td>${a.purchased_date||'—'}</td>
      <td style="color:${a.allocated_date?'#1d4ed8':'#94a3b8'};font-weight:${a.allocated_date?'600':'400'}">${a.allocated_date||'—'}</td>
      <td><span class="badge b-${a.status}">${a.status||'—'}</span></td>`), 7)}
    </tbody>
  </table>

  <!-- Accessories -->
  <div class="sect-hdr">Accessories &nbsp;<span style="font-weight:400;opacity:0.8">(${accessories.length})</span></div>
  <table>
    <thead><tr><th>Name</th><th>Category</th><th>Brand / Model</th><th>Serial No</th><th>Status</th></tr></thead>
    <tbody>
    ${tblRows(accessories.map(a=>`
      <td>${a.name||`${a.brand||''} ${a.model||''}`}</td>
      <td>${a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())||'—'}</td>
      <td>${a.brand||''} ${a.model||''}</td>
      <td class="mono">${a.serial_number||'—'}</td>
      <td><span class="badge b-${a.status}">${a.status||'—'}</span></td>`), 5)}
    </tbody>
  </table>

  <!-- System Logins -->
  <div class="sect-hdr">System Logins &nbsp;<span style="font-weight:400;opacity:0.8">(${itItems.systems?.length||0})</span></div>
  <table>
    <thead><tr><th>System</th><th>System URL</th><th>Username</th><th>Access Level</th><th>Approved By</th><th>Status</th></tr></thead>
    <tbody>
    ${tblRows((itItems.systems||[]).map(s=>`
      <td>${s.system_name||'—'}</td>
      <td>${s.system_url ? `<a href="${s.system_url}" style="color:#1d4ed8;">${s.system_url}</a>` : '—'}</td>
      <td class="mono">${s.username||'—'}</td>
      <td>${s.access_level||'—'}</td>
      <td>${s.approved_by||'—'}</td>
      <td><span class="badge b-${s.status}">${s.status||'—'}</span></td>`), 6)}
    </tbody>
  </table>

  <!-- Email Accounts -->
  <div class="sect-hdr">Email Accounts &nbsp;<span style="font-weight:400;opacity:0.8">(${itItems.emails?.length||0})</span></div>
  <table>
    <thead><tr><th>Email Address</th><th>Type</th><th>Approved By</th><th>Status</th></tr></thead>
    <tbody>
    ${tblRows((itItems.emails||[]).map(e=>`
      <td>${e.email_address||'—'}</td>
      <td>${e.email_type||'—'}</td>
      <td>${e.approved_by||'—'}</td>
      <td><span class="badge b-${e.status}">${e.status||'—'}</span></td>`), 4)}
    </tbody>
  </table>

  ${voice.length > 0 ? `
  <!-- Voice Numbers -->
  <div class="sect-hdr">Voice Numbers &nbsp;<span style="font-weight:400;opacity:0.8">(${voice.length})</span></div>
  <table>
    <thead><tr><th>Number</th><th>ISP</th><th>Package</th><th>Company</th><th>Status</th></tr></thead>
    <tbody>
    ${tblRows(voice.map(v=>`
      <td class="mono">${v.number||'—'}</td>
      <td>${v.isp_name||'—'}</td>
      <td>${v.package_name||'—'}</td>
      <td>${v.company_name||'—'}</td>
      <td><span class="badge b-${v.status}">${v.status||'—'}</span></td>`), 5)}
    </tbody>
  </table>` : ''}

  ${dongles.length > 0 ? `
  <!-- Dongles -->
  <div class="sect-hdr">Dongles &nbsp;<span style="font-weight:400;opacity:0.8">(${dongles.length})</span></div>
  <table>
    <thead><tr><th>Number</th><th>ISP</th><th>Package</th><th>Account No</th><th>Status</th></tr></thead>
    <tbody>
    ${tblRows(dongles.map(d=>`
      <td class="mono">${d.number||'—'}</td>
      <td>${d.isp_name||'—'}</td>
      <td>${d.package_name||'—'}</td>
      <td>${d.account_number||'—'}</td>
      <td><span class="badge b-${d.status}">${d.status||'—'}</span></td>`), 5)}
    </tbody>
  </table>` : ''}

  <!-- Footer -->
  <div class="footer">
    <span>SynergyNode IT Asset Management &nbsp;·&nbsp; CL Synergy Group</span>
    <span>Confidential &nbsp;·&nbsp; Generated ${genDate}</span>
  </div>

</div>
</body></html>`;

  const w = window.open('', '_blank', 'width=820,height=1000');
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 500);
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Employees() {
  const { isIT, isAdmin, isDirector, isViewOnly } = useAuth();
  // Directors and finance_staff see IT Profile / Ticket History read-only (isIT = false keeps edit buttons hidden)
  const canViewITProfile = isIT || isDirector || isViewOnly;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterStatus, setFilterStatus] = useState('');
  const [filterCompany, setFilterCompany] = useState(''); const [filterDept, setFilterDept] = useState('');
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [savedEmpId, setSavedEmpId] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [empCompanyAccess, setEmpCompanyAccess] = useState([]); // multi-company for linked system user
  const [companies, setCompanies] = useState([]); const [departments, setDepartments] = useState([]); const [employees, setEmployees] = useState([]); const [hodList, setHodList] = useState([]);

  // Profile view state
  const [profileEmp, setProfileEmp] = useState(null);
  const [profileAssets, setProfileAssets] = useState([]);
  const [profileAccessories, setProfileAccessories] = useState([]);
  const [profileVoice, setProfileVoice] = useState([]);
  const [profileDongles, setProfileDongles] = useState([]);
  const [profileConnHistory, setProfileConnHistory] = useState([]);
  const [profileItItems, setProfileItItems] = useState({ systems: [], emails: [] });
  const [profileTickets, setProfileTickets] = useState([]);
  const [profileRepairs, setProfileRepairs] = useState([]);
  const [profileItemHistory, setProfileItemHistory] = useState({ assets: [], accessories: [] });
  const [profileDamages, setProfileDamages] = useState([]);
  const [profileAccReqs, setProfileAccReqs] = useState([]);
  const [profileLoading, setProfileLoading] = useState(false);
  // Connection history mini-modal state
  const [connHistModal, setConnHistModal] = useState(null); // { number, type, id }
  const [connHist, setConnHist] = useState([]); const [connHistLoading, setConnHistLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search; if (filterStatus) params.status = filterStatus;
      if (filterCompany) params.company_id = filterCompany; if (filterDept) params.department_id = filterDept;
      const r = await api.get('/employees', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterStatus, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/settings/companies').then(r => setCompanies(r.data || [])).catch(() => {});
    api.get('/settings/departments').then(r => setDepartments(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
    api.get('/employees/meta/hods').then(r => setHodList(r.data || [])).catch(() => {});
  }, []);

  const openProfile = async (emp) => {
    setProfileEmp(emp);
    setModal('profile');
    setProfileLoading(true);
    try {
      const [assets, accs, voice, dongles, connHistory, itItems, tickets, repairs, damages, accReqs, itemHistory] = await Promise.all([
        api.get('/assets', { params: { limit: 200, holder_id: emp.id } }).then(r => r.data.data || []).catch(() => []),
        api.get('/accessories', { params: { limit: 200, assigned_to: emp.id } }).then(r => r.data.data || []).catch(() => []),
        api.get('/connections/voice', { params: { holder_id: emp.id, limit: 50 } }).then(r => r.data.data || []).catch(() => []),
        api.get('/connections/dongles', { params: { holder_id: emp.id, limit: 50 } }).then(r => r.data.data || []).catch(() => []),
        api.get(`/connections/employee-history/${emp.id}`).then(r => r.data || []).catch(() => []),
        api.get(`/master/employee-items/${emp.id}`).then(r => r.data).catch(() => ({ systems: [], emails: [] })),
        api.get('/tickets', { params: { employee_id: emp.id, limit: 100 } }).then(r => r.data.tickets || r.data.data || []).catch(() => []),
        api.get('/repairs', { params: { reported_by: emp.id, limit: 100 } }).then(r => r.data.data || []).catch(() => []),
        api.get(`/damages/employee/${emp.id}`).then(r => r.data.damages || []).catch(() => []),
        api.get('/accessory-requests', { params: { employee_id: emp.id, limit: 100 } }).then(r => r.data.data || []).catch(() => []),
        // Everything ever held — including items since returned, released or derecognized
        api.get(`/employees/${emp.id}/item-history`).then(r => r.data).catch(() => ({ assets: [], accessories: [] })),
      ]);
      setProfileAssets(assets);
      setProfileAccessories(accs);
      setProfileVoice(voice);
      setProfileDongles(dongles);
      setProfileConnHistory(connHistory);
      setProfileItItems(itItems);
      setProfileTickets(tickets);
      setProfileRepairs(repairs);
      setProfileDamages(damages);
      setProfileAccReqs(accReqs);
      setProfileItemHistory(itemHistory);
    } catch {} finally { setProfileLoading(false); }
  };

  const openConnHistory = async (conn, type) => {
    setConnHistModal({ number: conn.number, type, id: conn.id });
    setConnHistLoading(true); setConnHist([]);
    const h = await api.get(`/connections/history/${type}/${conn.id}`).then(r => r.data || []).catch(() => []);
    setConnHist(h); setConnHistLoading(false);
  };

  const filteredDepts = filterCompany ? departments.filter(d => d.company_id === filterCompany) : departments;
  const modalDepts = form.company_id ? departments.filter(d => d.company_id === form.company_id) : [];

  const openFormForEdit = async (r) => {
    setSelected(r); setForm({...r}); setError(''); setEmpCompanyAccess([]);
    // Load linked system user's company access
    const linkedUser = await api.get(`/settings/user-by-employee/${r.id}`).then(x => x.data).catch(() => null);
    if (linkedUser) {
      const acc = await api.get(`/master-files/user-access/${linkedUser.id}`).then(x => x.data).catch(() => []);
      setEmpCompanyAccess(acc.map(a => a.company_id));
    }
    setModal('form');
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.designation?.trim()) { setError('Designation is required.'); return; }
    if (!form.email?.trim()) { setError('Email is required.'); return; }
    if (!form.company_id) { setError('Company is required.'); return; }
    if (!form.department_id) { setError('Department is required.'); return; }
    if (!selected && !form.synergy_username?.trim()) { setError('SynergyNode Username is required for portal access.'); return; }
    setSaving(true); setError('');
    try {
      if (selected) {
        await api.put(`/employees/${selected.id}`, form);
        // Sync company access to linked system user
        const linkedUser = await api.get(`/settings/user-by-employee/${selected.id}`).then(x => x.data).catch(() => null);
        if (linkedUser && empCompanyAccess.length > 0) {
          await api.post(`/master-files/user-access/${linkedUser.id}`, { company_ids: empCompanyAccess }).catch(() => {});
        }
        setModal(null); setSelected(null); load();
        // Prompt to create offboarding record when marking resigned/terminated
        if (['resigned','terminated'].includes(form.status) && !['resigned','terminated'].includes(selected.status)) {
          if (window.confirm(`${selected.full_name} has been marked as ${form.status}.\n\nWould you like to create a formal Offboarding record to track asset returns and access revocation?`)) {
            window.location.href = `/offboarding`;
          }
        }
      } else {
        const res = await api.post('/employees', form);
        setSavedEmpId(res.data.id);
        setModal('it_profile');
        load();
      }
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const ProfileSection = ({ icon: Icon, color, title, count, children }) => (
    <div className="rounded-xl overflow-hidden mb-3" style={{border:`1px solid ${color}25`}}>
      <div className="flex items-center gap-2 px-4 py-2.5" style={{background:`${color}10`}}>
        <Icon size={13} style={{color}}/>
        <span className="text-sm font-semibold" style={{color:'#94a3b8'}}>{title}</span>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full" style={{background:'rgba(255,255,255,0.06)',color:'#475569'}}>{count}</span>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );

  const columns = [
    { header: 'Name', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.full_name}</div><div className="text-xs" style={{color:'#334155'}}>{r.employee_code||''}</div></div> },
    { header: 'Designation', render: r => <span style={{color:'#94a3b8'}}>{r.designation||'—'}</span> },
    { header: 'Department', render: r => <span style={{color:'#94a3b8'}}>{r.department?.name||'—'}</span> },
    { header: 'Company', render: r => <span className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.company?.code||'—'}</span> },
    { header: 'Email', render: r => <span className="text-xs" style={{color:'#475569'}}>{r.email||'—'}</span> },
    { header: 'Status', render: r => <Badge value={r.status}/> },
    { header: '', render: r => (
      <div className="flex gap-1">
        <button onClick={() => openProfile(r)} className="btn-ghost px-2 py-1 text-xs" title="View Full Profile"><Eye size={11}/></button>
        {isIT && <button onClick={() => openFormForEdit(r)} className="btn-ghost px-2 py-1 text-xs" title="Edit"><Edit2 size={11}/></button>}
        {isAdmin && <button onClick={async () => { if(window.confirm('Remove?')) { await api.delete(`/employees/${r.id}`); load(); }}} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}} title="Delete"><Trash2 size={11}/></button>}
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Employees" count={total}>
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
        {isIT && <button onClick={() => { setSelected(null); setSavedEmpId(null); setForm({status:'active'}); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add Employee</button>}
      </PageHeader>

      <FilterBar hasFilters={!!(search||filterStatus||filterCompany||filterDept)} onClear={() => { setSearch(''); setFilterStatus(''); setFilterCompany(''); setFilterDept(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search name, email, employee code..."/>
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s=>({value:s,label:s}))}/>
        <FilterSelect value={filterCompany} onChange={v => { setFilterCompany(v); setFilterDept(''); setPage(1); }} placeholder="All Companies" options={companies.map(c=>({value:c.id,label:c.name}))}/>
        <FilterSelect value={filterDept} onChange={v => { setFilterDept(v); setPage(1); }} placeholder="All Departments" options={filteredDepts.map(d=>({value:d.id,label:d.name}))}/>
      </FilterBar>

      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>

      {/* ── PROFILE VIEW ── */}
      {modal === 'profile' && profileEmp && (
        <Modal title="" onClose={() => { setModal(null); setProfileEmp(null); }} size="xl">
          {/* Header */}
          <div className="flex items-start gap-4 pb-4 mb-4" style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-xl font-bold text-white"
              style={{background:'linear-gradient(135deg,#3b82f6,#8b5cf6)'}}>
              {profileEmp.full_name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xl font-bold" style={{color:'#f1f5f9'}}>{profileEmp.full_name}</div>
              <div className="text-sm mt-0.5" style={{color:'#60a5fa'}}>{profileEmp.designation||'—'}</div>
              <div className="flex flex-wrap gap-3 mt-2 text-xs" style={{color:'#475569'}}>
                {profileEmp.employee_code && <span className="flex items-center gap-1"><Hash size={10}/>{profileEmp.employee_code}</span>}
                {profileEmp.department?.name && <span className="flex items-center gap-1"><Briefcase size={10}/>{profileEmp.department.name}</span>}
                {profileEmp.company?.name && <span className="flex items-center gap-1"><Building2 size={10}/>{profileEmp.company.name}</span>}
                {profileEmp.email && <span className="flex items-center gap-1"><Mail size={10}/>{profileEmp.email}</span>}
                {profileEmp.phone && <span className="flex items-center gap-1"><Phone size={10}/>{profileEmp.phone}</span>}
                {profileEmp.date_joined && <span className="flex items-center gap-1"><Calendar size={10}/>Joined: {profileEmp.date_joined}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Badge value={profileEmp.status}/>
              <button
                onClick={() => downloadProfilePDF(profileEmp, profileAssets, profileAccessories, profileItItems, profileVoice, profileDongles)}
                className="btn-primary text-xs flex items-center gap-1.5">
                <Download size={12}/> PDF
              </button>
            </div>
          </div>

          {profileLoading ? (
            <div className="flex items-center justify-center py-12 gap-3" style={{color:'#334155'}}>
              <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>
              Loading profile...
            </div>
          ) : (
            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">

              {/* Assets */}
              <ProfileSection icon={Monitor} color="#3b82f6" title="Assigned Assets" count={profileAssets.length}>
                {profileAssets.length === 0 ? <div className="text-xs text-center py-3" style={{color:'#334155'}}>No assets assigned</div> : (
                  <div className="space-y-1.5">
                    {profileAssets.map((a,i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{a.asset_number}</span>
                            <span className="text-xs" style={{color:'#475569'}}>{a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                          </div>
                          <div className="text-sm" style={{color:'#e2e8f0'}}>{a.brand} {a.model}</div>
                          <div className="text-xs" style={{color:'#334155'}}>
                            S/N: {a.serial_number||'—'}
                            {a.allocated_date ? ` · Allocated: ${a.allocated_date}` : a.purchased_date ? ` · Purchased: ${a.purchased_date}` : ''}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge value={a.status}/>
                          {a.warranty_period && a.warranty_period !== 'none' && <WarrantyBadge warrantyPeriod={a.warranty_period} purchasedDate={a.purchased_date} small/>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ProfileSection>

              {/* Accessories */}
              <ProfileSection icon={Package} color="#8b5cf6" title="Accessories" count={profileAccessories.length}>
                {profileAccessories.length === 0 ? <div className="text-xs text-center py-3" style={{color:'#334155'}}>No accessories assigned</div> : (
                  <div className="space-y-1.5">
                    {profileAccessories.map((a,i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm" style={{color:'#e2e8f0'}}>{a.name||`${a.brand} ${a.model}`}</div>
                          <div className="text-xs" style={{color:'#475569'}}>
                            {a.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}{a.serial_number?` · S/N: ${a.serial_number}`:''}
                            {a.allocated_date ? <span style={{color:'#1d4ed8',fontWeight:500}}> · Allocated: {a.allocated_date}</span> : ''}
                          </div>
                        </div>
                        <Badge value={a.status}/>
                      </div>
                    ))}
                  </div>
                )}
              </ProfileSection>

              {/* IT Profile — add/view system logins + emails (visible to director/finance read-only) */}
              {canViewITProfile && <ITProfileSection empId={profileEmp.id} isIT={isIT}/>}

              {/* IT Tickets — visible to director/finance read-only */}
              {/* Everything this employee has ever held, with the period used */}
              <ProfileSection icon={History} color="#14b8a6" title="Asset & Accessory History" count={itemHistoryCount(profileItemHistory)}>
                <ItemHistoryList history={profileItemHistory}
                  emptyMsg="No assets or accessories have been allocated to this employee"/>
              </ProfileSection>

              {canViewITProfile && <ProfileSection icon={FileText} color="#f59e0b" title="IT Ticket History" count={profileTickets.length}>
                {profileTickets.length === 0 ? (
                  <div className="text-xs text-center py-3" style={{color:'#334155'}}>No IT tickets raised</div>
                ) : (
                  <div className="space-y-1.5">
                    {/* Summary row for admin/IT only */}
                    {(isAdmin || isIT) && (() => {
                      const withDamage = profileTickets.filter(t => t.is_physical_damage && t.damage_repair_cost);
                      const totalCost = withDamage.reduce((s, t) => s + Number(t.damage_repair_cost || 0), 0);
                      const userPaid = withDamage.filter(t => isEmployeePaid(t.damage_payment_method));
                      const userCost = userPaid.reduce((s, t) => s + Number(t.damage_repair_cost || 0), 0);
                      return withDamage.length > 0 ? (
                        <div className="flex gap-3 mb-2 text-xs flex-wrap">
                          <span className="px-2 py-1 rounded-md" style={{background:'rgba(239,68,68,0.1)',color:'#f87171'}}>
                            {withDamage.length} damage repair{withDamage.length > 1 ? 's' : ''} · Total LKR {totalCost.toLocaleString()}
                          </span>
                          {userPaid.length > 0 && (
                            <span className="px-2 py-1 rounded-md" style={{background:'rgba(245,158,11,0.1)',color:'#fbbf24'}}>
                              Employee liable: LKR {userCost.toLocaleString()}
                            </span>
                          )}
                        </div>
                      ) : null;
                    })()}
                    {profileTickets.map((t, i) => (
                      <div key={i} className="p-2.5 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{t.ticket_number}</span>
                          <Badge value={t.status}/>
                          <Badge value={t.priority}/>
                          {t.board_approval_status === 'approved' && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{background:'rgba(22,163,74,0.15)',color:'#4ade80',border:'1px solid rgba(22,163,74,0.3)',fontSize:9.5}}>✓ Board Approved</span>}
                          {t.board_approval_status === 'rejected' && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{background:'rgba(220,38,38,0.15)',color:'#f87171',border:'1px solid rgba(220,38,38,0.3)',fontSize:9.5}}>✖ Board Rejected</span>}
                          {t.board_approval_status === 'pending' && <span className="text-xs px-1.5 py-0.5 rounded-full" style={{background:'rgba(124,58,237,0.15)',color:'#a78bfa',border:'1px solid rgba(124,58,237,0.3)',fontSize:9.5}}>⏳ Board Pending</span>}
                          <TicketElapsedBadge ticket={t}/>
                          <span className="text-xs ml-auto" style={{color:'#334155'}}>{t.reported_date ? new Date(t.reported_date).toLocaleDateString('en-GB') : '—'}</span>
                        </div>
                        <div className="text-sm mt-1" style={{color:'#e2e8f0'}}>{t.title}</div>
                        <div className="text-xs mt-0.5" style={{color:'#475569'}}>{t.category?.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}{t.relatedAsset ? ` · ${t.relatedAsset.asset_number}` : ''}</div>
                        {/* Repair / damage info */}
                        {t.is_physical_damage && (
                          <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs">
                            <AlertTriangle size={11} style={{color:'#f87171'}}/>
                            <span style={{color:'#f87171'}}>Physical damage</span>
                            {/* Show cost to admin/IT always; show cost to others only if user-paid */}
                            {(isAdmin || isIT) && t.damage_repair_cost && (
                              <span style={{color:'#f87171'}}>· Repair: LKR {Number(t.damage_repair_cost).toLocaleString()}</span>
                            )}
                            {(isAdmin || isIT) && t.damage_payment_method && (
                              <span style={{color: damagePayerColor(t.damage_payment_method)}}>
                                · {damagePayerLabel(t.damage_payment_method)}
                              </span>
                            )}
                            {/* Non-admin/IT: show cost only if charged to employee */}
                            {!(isAdmin || isIT) && isEmployeePaid(t.damage_payment_method) && t.damage_repair_cost && (
                              <span style={{color:'#fbbf24'}}>· LKR {Number(t.damage_repair_cost).toLocaleString()} charged to you</span>
                            )}
                          </div>
                        )}
                        {t.resolution_notes && (t.status === 'resolved' || t.status === 'closed') && (
                          <div className="mt-1 text-xs" style={{color:'#334155'}}>Resolution: {t.resolution_notes}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </ProfileSection>}

              {/* Repairs */}
              {canViewITProfile && profileRepairs.length > 0 && (
                <ProfileSection icon={Wrench} color="#fbbf24" title="Repairs" count={profileRepairs.length}>
                  <div className="space-y-1.5">
                    {profileRepairs.map((r,i) => (
                      <div key={r.id||i} className="p-2.5 rounded-lg text-xs" style={{background:'rgba(245,158,11,0.05)',border:'1px solid rgba(245,158,11,0.15)'}}>
                        <div className="flex items-center justify-between">
                          <span style={{color:'#e2e8f0'}}>{r.issue_description||'—'}</span>
                          <Badge value={r.status}/>
                        </div>
                        <div className="mt-1" style={{color:'#94a3b8'}}>
                          {r.Asset ? `${r.Asset.asset_number} · ` : ''}
                          {r.repair_vendor ? `${r.repair_vendor} · ` : ''}
                          {r.repair_cost ? `LKR ${Number(r.repair_cost).toLocaleString()} · ` : ''}
                          {r.reported_date||'—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </ProfileSection>
              )}

              {/* Damage Records (standalone, not tied to a ticket) */}
              {canViewITProfile && profileDamages.filter(d => !d.ticket_id).length > 0 && (
                <ProfileSection icon={AlertTriangle} color="#f87171" title="Damage Records" count={profileDamages.filter(d => !d.ticket_id).length}>
                  <div className="space-y-1.5">
                    {profileDamages.filter(d => !d.ticket_id).map((d,i) => (
                      <div key={d.id||i} className="p-2.5 rounded-lg text-xs" style={{background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.15)'}}>
                        <div className="flex items-center justify-between">
                          <span style={{color:'#e2e8f0'}}>{d.damage_type||'Damage'}{d.asset ? ` — ${d.asset.asset_number}` : ''}</span>
                          <Badge value={d.payment_status}/>
                        </div>
                        <div className="mt-1" style={{color:'#94a3b8'}}>
                          LKR {Number(d.repair_cost||0).toLocaleString()} · {(d.payment_method||'').replace(/_/g,' ')} · {d.incident_date||'—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </ProfileSection>
              )}

              {/* Accessory Requests */}
              {canViewITProfile && profileAccReqs.length > 0 && (
                <ProfileSection icon={ClipboardList} color="#818cf8" title="Accessory Requests" count={profileAccReqs.length}>
                  <div className="space-y-1.5">
                    {profileAccReqs.map((a,i) => (
                      <div key={a.id||i} className="p-2.5 rounded-lg text-xs" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div className="flex items-center justify-between">
                          <span className="font-mono" style={{color:'#60a5fa'}}>{a.request_number}</span>
                          <Badge value={a.status}/>
                        </div>
                        <div className="mt-1" style={{color:'#94a3b8'}}>
                          {[...(a.requested_items||[]), a.other_item].filter(Boolean).join(', ')||'—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </ProfileSection>
              )}

              {/* Voice */}
              {profileVoice.length > 0 && (
                <ProfileSection icon={PhoneCall} color="#10b981" title="Voice Numbers" count={profileVoice.length}>
                  <div className="space-y-1.5">
                    {profileVoice.map((v,i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div className="flex-1">
                          <div className="font-mono text-sm" style={{color:'#e2e8f0'}}>{v.number}</div>
                          <div className="text-xs" style={{color:'#475569'}}>{v.isp_name||'—'}{v.package_name?` · ${v.package_name}`:''}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge value={v.status}/>
                          <button onClick={() => openConnHistory(v,'voice')} title="View allocation history" className="btn-ghost p-1" style={{color:'#818cf8'}}><Eye size={12}/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ProfileSection>
              )}

              {/* Dongles */}
              {profileDongles.length > 0 && (
                <ProfileSection icon={Wifi} color="#06b6d4" title="Dongles" count={profileDongles.length}>
                  <div className="space-y-1.5">
                    {profileDongles.map((d,i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                        <div className="flex-1">
                          <div className="font-mono text-sm" style={{color:'#e2e8f0'}}>{d.number}</div>
                          <div className="text-xs" style={{color:'#475569'}}>{d.isp_name||'—'}{d.account_number?` · Acc: ${d.account_number}`:''}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge value={d.status}/>
                          <button onClick={() => openConnHistory(d,'dongle')} title="View allocation history" className="btn-ghost p-1" style={{color:'#818cf8'}}><Eye size={12}/></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ProfileSection>
              )}

              {/* Connection History */}
              {profileConnHistory.length > 0 && (
                <ProfileSection icon={Eye} color="#818cf8" title="Connection History" count={profileConnHistory.length}>
                  <div className="space-y-1.5">
                    {profileConnHistory.slice(0,10).map((h,i) => {
                      const aColor = { assigned:'#34d399', transferred:'#60a5fa', reallocated:'#a78bfa', returned:'#f59e0b', disconnected:'#ef4444', suspended:'#f59e0b' }[h.action] || '#94a3b8';
                      const aLabel = { assigned:'Assigned', transferred:'Transferred', reallocated:'Reallocated', returned:'Returned', disconnected:'Disconnected', suspended:'Suspended' }[h.action] || h.action;
                      return (
                        <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-xs font-semibold" style={{color:'#818cf8'}}>{h.number}</span>
                              <span className="text-xs capitalize px-1.5 py-0.5 rounded" style={{background:`${aColor}15`,color:aColor,fontSize:10}}>{aLabel}</span>
                              <span className="text-xs" style={{color:'#334155'}}>{h.connection_type}</span>
                            </div>
                            <div className="text-xs mt-0.5 truncate" style={{color:'#475569'}}>
                              {h.from_name && <span>{h.from_name} → </span>}{h.to_name || '—'}
                              {h.isp_name && <span style={{color:'#334155'}}> · {h.isp_name}</span>}
                            </div>
                          </div>
                          <div className="text-xs whitespace-nowrap" style={{color:'#334155'}}>
                            {new Date(h.transfer_date).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                      );
                    })}
                    {profileConnHistory.length > 10 && <div className="text-xs text-center py-1" style={{color:'#334155'}}>+{profileConnHistory.length - 10} more entries</div>}
                  </div>
                </ProfileSection>
              )}
            </div>
          )}
        </Modal>
      )}

      {/* ── CONNECTION HISTORY MINI-MODAL ── */}
      {connHistModal && (
        <Modal title={`Allocation History — ${connHistModal.number}`} onClose={() => { setConnHistModal(null); setConnHist([]); }} size="lg">
          {connHistLoading ? (
            <div className="py-8 text-center text-xs" style={{color:'#334155'}}>Loading...</div>
          ) : connHist.length === 0 ? (
            <div className="py-8 text-center text-xs" style={{color:'#1e3a5f'}}>No history recorded</div>
          ) : (
            <div className="space-y-2">
              {connHist.map((h, i) => {
                const aColor = { assigned:'#34d399', transferred:'#60a5fa', reallocated:'#a78bfa', returned:'#f59e0b', disconnected:'#ef4444', suspended:'#f59e0b', reconnected:'#10b981' }[h.action] || '#94a3b8';
                const aLabel = { assigned:'Assigned', transferred:'Transferred', reallocated:'Reallocated', returned:'Returned', disconnected:'Disconnected', suspended:'Suspended', reconnected:'Reconnected' }[h.action] || h.action;
                return (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.05)'}}>
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{background:`${aColor}18`,color:aColor}}>{(aLabel[0]||'').toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold" style={{color:aColor}}>{aLabel}</div>
                      <div className="text-xs mt-0.5" style={{color:'#475569'}}>
                        {h.from_name && <><span style={{color:'#94a3b8'}}>{h.from_name}</span><span style={{color:'#334155'}}> → </span></>}
                        {h.to_name ? <span style={{color:'#e2e8f0'}}>{h.to_name}</span> : <span style={{color:'#334155'}}>—</span>}
                        {h.package_name && <span style={{color:'#334155'}}> · {h.package_name}</span>}
                      </div>
                      {h.remarks && <div className="text-xs mt-0.5 truncate" style={{color:'#334155'}}>{h.remarks}</div>}
                    </div>
                    <div className="text-xs whitespace-nowrap flex-shrink-0" style={{color:'#1e3a5f'}}>{new Date(h.transfer_date).toLocaleDateString('en-GB')}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}

      {/* ── EMPLOYEE FORM ── */}
      {modal === 'form' && (
        <Modal title={selected ? `Edit: ${selected.full_name}` : 'Add Employee'} onClose={() => { setModal(null); setSelected(null); }} size="xl">
          <form onSubmit={handleSave} className="space-y-5">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <FF label="Full Name *" name="full_name" form={form} setForm={setForm} required/>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Employee Code <span style={{color:'#334155'}}>(auto-generated)</span></label>
                <input value={form.employee_code||''} readOnly className="input-dark" style={{opacity:0.6,cursor:'not-allowed'}} placeholder="Will be auto-generated on save"/>
              </div>
              <FF label="Designation *" name="designation" form={form} setForm={setForm} required/>
              <FF label="Email *" name="email" type="email" form={form} setForm={setForm} required/>
              <FF label="Phone" name="phone" form={form} setForm={setForm}/>
              <FF label="Date Joined" name="date_joined" type="date" form={form} setForm={setForm}/>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company *</label>
                <SearchableSelect required value={form.company_id||''} onChange={v=>setForm({...form,company_id:v||undefined,department_id:undefined})}
                  options={companies.map(c=>({value:c.id,label:c.name}))} placeholder="Select company"/>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Department *</label>
                <SearchableSelect required value={form.department_id||''} onChange={v=>setForm({...form,department_id:v||undefined})}
                  options={modalDepts.map(d=>({value:d.id,label:d.name}))} placeholder={form.company_id?'Select department':'— Select company first —'} disabled={!form.company_id}/>
              </div>
              <FS label="Status" name="status" form={form} setForm={setForm} options={STATUSES.map(s=>({val:s,label:s}))}/>
              <div className="col-span-2 flex items-center gap-2 px-3 py-2 rounded-lg" style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.18)'}}>
                <input type="checkbox" id="is_hod_chk" checked={!!form.is_hod} onChange={e=>setForm({...form,is_hod:e.target.checked})} className="accent-indigo-500 w-4 h-4"/>
                <label htmlFor="is_hod_chk" className="text-xs font-medium cursor-pointer" style={{color:'#a5b4fc'}}>This employee is a Head of Department (HOD)</label>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>HOD (Head of Department)</label>
                <SearchableSelect value={form.hod_id||''} onChange={v=>setForm({...form,hod_id:v||undefined})}
                  options={hodList.map(e=>({value:e.id,label:e.full_name+(e.designation?` — ${e.designation}`:'')}))}/>
              </div>
              <FF label="HOD Email (override)" name="hod_email" type="email" form={form} setForm={setForm} placeholder="Leave blank to use HOD's profile email"/>
            </div>
            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={2}/>

            {/* Multi-company access — shown when editing (syncs to linked system user) */}
            {selected && (
              <div className="rounded-xl p-4" style={{background:'rgba(59,130,246,0.04)',border:'1px solid rgba(59,130,246,0.15)'}}>
                <div className="text-xs font-semibold mb-2" style={{color:'#60a5fa'}}>Additional Company Access (SynergyNode Login)</div>
                <div className="text-xs mb-3" style={{color:'#334155'}}>Select additional companies this employee's portal login can view data from.</div>
                <div className="grid grid-cols-2 gap-2">
                  {companies.map(c => (
                    <label key={c.id} className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer"
                      style={{background: empCompanyAccess.includes(c.id) ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.03)', border:`1px solid ${empCompanyAccess.includes(c.id)?'rgba(59,130,246,0.35)':'rgba(255,255,255,0.06)'}`}}>
                      <input type="checkbox" checked={empCompanyAccess.includes(c.id)}
                        onChange={e => setEmpCompanyAccess(prev => e.target.checked ? [...prev, c.id] : prev.filter(id => id !== c.id))}
                        className="accent-blue-500"/>
                      <div>
                        <div className="text-xs font-medium" style={{color:'#e2e8f0'}}>{c.name}</div>
                        <div className="text-xs font-mono" style={{color:'#475569'}}>{c.code}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* SynergyNode login — only for new employees */}
            {!selected && (
              <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(59,130,246,0.3)'}}>
                <div className="px-4 py-3 flex items-center gap-2" style={{background:'rgba(29,78,216,0.12)'}}>
                  <span className="text-sm font-semibold" style={{color:'#60a5fa'}}>SynergyNode Portal Access</span>
                  <span className="text-xs ml-1" style={{color:'#334155'}}>Auto-created on save</span>
                </div>
                <div className="p-4">
                  <FF label="SynergyNode Username *" name="synergy_username" form={form} setForm={setForm}
                    placeholder="e.g. niranja.d or EMP001" required/>
                  <div className="text-xs mt-2 px-1" style={{color:'#475569'}}>
                    A random secure password will be generated and emailed to the employee along with the system URL.
                    The employee must change it on first login.
                  </div>
                </div>
              </div>
            )}
            {/* IT Profile — editable in edit mode; multiple entries supported */}
            {selected && (
              <div className="rounded-xl p-3" style={{background:'rgba(59,130,246,0.04)',border:'1px solid rgba(59,130,246,0.15)'}}>
                <div className="mb-2">
                  <span className="text-xs font-semibold" style={{color:'#60a5fa'}}>IT Profile (System Logins &amp; Emails)</span>
                  <span className="text-xs ml-2" style={{color:'#334155'}}>Add as many logins or email IDs as needed</span>
                </div>
                <ITProfileSection empId={selected.id} isIT={isIT}/>
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">
                {saving ? 'Saving...' : selected ? 'Save Changes' : 'Save & Add IT Profile →'}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── PHASE 2: IT Profile after new employee ── */}
      {modal === 'it_profile' && savedEmpId && (
        <Modal title="Employee Saved ✓ — Add IT Profile" onClose={() => { setModal(null); setSavedEmpId(null); }} size="xl">
          <div className="space-y-4">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{background:'rgba(16,185,129,0.08)',border:'1px solid rgba(16,185,129,0.2)'}}>
              <CheckCircle size={18} style={{color:'#34d399'}}/>
              <div>
                <div className="font-medium text-sm" style={{color:'#34d399'}}>Employee created successfully</div>
                <div className="text-xs" style={{color:'#334155'}}>Add system logins and email IDs below. These also appear in Access Management.</div>
              </div>
            </div>
            <ITProfileSection empId={savedEmpId} isIT={isIT}/>
            {/* Onboarding next steps */}
            <div className="rounded-xl p-4" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.2)'}}>
              <div className="text-xs font-semibold mb-2" style={{color:'#60a5fa'}}>Onboarding — Next Steps</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  ['Allocate an asset', '/in-hand', '🖥'],
                  ['Allocate accessories', '/in-hand', '🎒'],
                  ['Assign dongle / voice number', '/dongles', '📶'],
                  ['Create SynergyNode login', '/settings', '🔑'],
                ].map(([label, path, icon]) => (
                  <button key={label} onClick={() => { window.location.href = path; }}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-left"
                    style={{background:'rgba(255,255,255,0.04)',color:'#94a3b8',border:'1px solid rgba(255,255,255,0.08)'}}>
                    <span>{icon}</span> {label}
                  </button>
                ))}
              </div>
              <div className="text-xs mt-2" style={{color:'#334155'}}>The welcome email with login details is sent automatically when the SynergyNode user account is created.</div>
            </div>
            <button onClick={() => { setModal(null); setSavedEmpId(null); }} className="btn-primary w-full justify-center">Done</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
